import { useState, useEffect, useRef } from 'react';
import { Bell, X, Check, CheckCheck, Music, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

export const NotificationBell = () => {
    const { token, user } = useAuth();
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const dropdownRef = useRef(null);

    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

    // Fetch notifications and count
    useEffect(() => {
        if (token && user?.role === 'submitter') {
            fetchUnreadCount();
            // Poll for new notifications every 30 seconds
            const interval = setInterval(fetchUnreadCount, 30000);
            return () => clearInterval(interval);
        }
    }, [token, user]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const fetchUnreadCount = async () => {
        try {
            const response = await fetch(`${API}/notifications/count`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setUnreadCount(data.count);
            }
        } catch (error) {
            console.error('Error fetching notification count:', error);
        }
    };

    const fetchNotifications = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API}/notifications?limit=20`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setNotifications(data);
            }
        } catch (error) {
            console.error('Error fetching notifications:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleOpen = () => {
        if (!isOpen) {
            fetchNotifications();
        }
        setIsOpen(!isOpen);
    };

    const markAsRead = async (notificationId) => {
        try {
            await fetch(`${API}/notifications/${notificationId}/read`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setNotifications(prev => 
                prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
            );
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    };

    const markAllAsRead = async () => {
        try {
            await fetch(`${API}/notifications/read-all`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setNotifications(prev => prev.map(n => ({ ...n, read: true })));
            setUnreadCount(0);
        } catch (error) {
            console.error('Error marking all notifications as read:', error);
        }
    };

    const handleNotificationClick = (notification) => {
        if (!notification.read) {
            markAsRead(notification.id);
        }
        // Navigate based on notification type
        if (notification.type === 'submission_status' && notification.data?.submission_id) {
            navigate(`/submissions/${notification.data.submission_id}`);
            setIsOpen(false);
        }
    };

    const formatTime = (isoString) => {
        const date = new Date(isoString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;
        return date.toLocaleDateString();
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'accepted': return 'text-green-400';
            case 'rejected': return 'text-red-400';
            default: return 'text-blue-400';
        }
    };

    const getStatusIcon = (notification) => {
        if (notification.data?.new_status === 'accepted') {
            return <Check className="w-4 h-4 text-green-400" />;
        }
        if (notification.data?.new_status === 'rejected') {
            return <AlertCircle className="w-4 h-4 text-red-400" />;
        }
        return <Music className="w-4 h-4 text-blue-400" />;
    };

    // Only show for submitters
    if (user?.role !== 'submitter') {
        return null;
    }

    return (
        <div className="relative" ref={dropdownRef}>
            {/* Bell Button */}
            <button
                onClick={handleOpen}
                className="relative p-2 rounded-lg text-[#A3A3A3] hover:text-white hover:bg-[#262626] transition-colors"
                data-testid="notification-bell"
            >
                <Bell size={20} />
                {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#F51E1E] rounded-full flex items-center justify-center text-xs font-bold text-white">
                        {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                )}
            </button>

            {/* Dropdown */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="absolute right-0 top-full mt-2 w-80 bg-[#1A1A1A] border border-[#333] rounded-xl shadow-xl overflow-hidden z-50"
                        data-testid="notification-dropdown"
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between px-4 py-3 border-b border-[#333]">
                            <h3 className="font-semibold text-white">Notifications</h3>
                            {unreadCount > 0 && (
                                <button
                                    onClick={markAllAsRead}
                                    className="text-xs text-[#F51E1E] hover:text-white transition-colors flex items-center gap-1"
                                >
                                    <CheckCheck size={14} />
                                    Mark all read
                                </button>
                            )}
                        </div>

                        {/* Notifications List */}
                        <div className="max-h-96 overflow-y-auto">
                            {loading ? (
                                <div className="flex items-center justify-center py-8">
                                    <div className="w-6 h-6 border-2 border-[#F51E1E] border-t-transparent rounded-full animate-spin" />
                                </div>
                            ) : notifications.length === 0 ? (
                                <div className="py-8 text-center">
                                    <Bell className="w-10 h-10 text-[#404040] mx-auto mb-3" />
                                    <p className="text-[#888] text-sm">No notifications yet</p>
                                </div>
                            ) : (
                                notifications.map((notification) => (
                                    <div
                                        key={notification.id}
                                        onClick={() => handleNotificationClick(notification)}
                                        className={`
                                            px-4 py-3 border-b border-[#262626] cursor-pointer transition-colors
                                            ${notification.read ? 'bg-transparent' : 'bg-[#262626]/50'}
                                            hover:bg-[#262626]
                                        `}
                                        data-testid={`notification-item-${notification.id}`}
                                    >
                                        <div className="flex items-start gap-3">
                                            <div className={`
                                                w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0
                                                ${notification.data?.new_status === 'accepted' ? 'bg-green-500/20' : 
                                                  notification.data?.new_status === 'rejected' ? 'bg-red-500/20' : 
                                                  'bg-blue-500/20'}
                                            `}>
                                                {getStatusIcon(notification)}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className={`text-sm font-medium ${notification.read ? 'text-[#A3A3A3]' : 'text-white'}`}>
                                                    {notification.title}
                                                </p>
                                                <p className="text-xs text-[#888] mt-0.5 line-clamp-2">
                                                    {notification.message}
                                                </p>
                                                <p className="text-xs text-[#666] mt-1">
                                                    {formatTime(notification.created_at)}
                                                </p>
                                            </div>
                                            {!notification.read && (
                                                <div className="w-2 h-2 bg-[#F51E1E] rounded-full flex-shrink-0 mt-2" />
                                            )}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>

                        {/* Footer */}
                        {notifications.length > 0 && (
                            <div className="px-4 py-2 border-t border-[#333]">
                                <button
                                    onClick={() => {
                                        navigate('/submissions');
                                        setIsOpen(false);
                                    }}
                                    className="w-full text-center text-sm text-[#F51E1E] hover:text-white transition-colors py-1"
                                >
                                    View all submissions
                                </button>
                            </div>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};
